<template>
    <div class="ticket-info-page">
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="top" timeout="3000">
        {{ snackbar.message }}
      </v-snackbar>
      <ResolveTicket
        v-model="resolveDialog"
        :ticket="ticket"
        @resolved="handleResolved"
      />
      <!-- TOP: Case header -->
      <div class="ticket-top">
        <div class="d-flex align-start justify-space-between" style="gap: 12px">
          <div class="flex-grow-1">
            <div class="text-h6 font-weight-bold mb-2">{{ ticket.subject }}</div>
  
            <div class="d-flex align-center flex-wrap header-meta" style="gap: 10px">
              <span class="text-body-2 text-medium-emphasis">
                Case ID: {{ ticket.caseId }}
              </span>
  
              <span class="meta-dot">•</span>
  
              <span class="text-body-2 text-medium-emphasis d-inline-flex align-center" style="gap: 6px">
                Severity:
                <v-chip
                  size="small"
                  variant="flat"
                  :color="severityChipColor"
                  class="font-weight-bold text-white"
                >
                  {{ ticket.severity }}
                </v-chip>
              </span>
  
              <span class="meta-dot">•</span>
  
              <v-chip
                size="small"
                variant="flat"
                :color="statusColor"
                class="text-uppercase font-weight-bold text-white"
                :prepend-icon="statusIcon"
              >
                {{ statusLabel }}
              </v-chip>
            </div>
          </div>
  
          <div class="d-flex align-center flex-wrap flex-shrink-0" style="gap: 8px">
            <v-btn
              v-if="!isResolved && !isAutoClosed"
              variant="outlined"
              color="primary"
              class="text-none"
              @click="resolveDialog = true"
            >
              Mark resolved
            </v-btn>
            <v-btn
              variant="text"
              color="primary"
              prepend-icon="mdi-arrow-left"
              class="text-none"
              @click="$emit('back')"
            >
              Back
            </v-btn>
          </div>
        </div>
  
        <v-divider class="mt-4" />
      </div>
  
      <!-- MIDDLE: Correspondence header fixed + messages scroll -->
      <div class="ticket-middle">
        <div class="correspondence-header">
          <div class="text-subtitle-1 font-weight-bold">Correspondence</div>
          <div class="d-flex align-center" style="gap: 16px">
            <div class="text-body-2 text-medium-emphasis">
              {{ messages.length }} {{ messages.length === 1 ? "entry" : "entries" }}
            </div>
            <v-btn
              variant="text"
              color="primary"
              size="small"
              class="text-none view-logs-btn"
              :prepend-icon="showChatLogs ? 'mdi-close' : 'mdi-message-text-outline'"
              @click="toggleChatLogs"
            >
              {{ showChatLogs ? "Close logs" : "View logs" }}
            </v-btn>
          </div>
        </div>
  
        <div ref="messagesScroll" class="messages-scroll">
          <div v-if="showChatLogs && !chatLogMessages.length" class="logs-empty">
            <v-icon size="18" color="grey">mdi-robot-outline</v-icon>
            <span>No chat logs for this case.</span>
          </div>

          <div v-if="visibleMessages.length" class="timeline">
            <div
              v-for="(msg, index) in visibleMessages"
              :key="msg.id || `${msg.time}-${index}`"
              class="timeline-item"
              :class="{ 'is-last': index === visibleMessages.length - 1 }"
            >
              <div class="timeline-rail">
                <span
                  class="timeline-node"
                  :class="{
                    'node-support': msg.type === 'support',
                    'node-bot': msg.type === 'bot',
                  }"
                ></span>
                <div v-if="index !== visibleMessages.length - 1" class="timeline-line"></div>
              </div>
  
              <div class="timeline-content">
                <div class="d-flex align-start" style="gap: 12px">
                  <v-avatar :color="avatarColor(msg.type)" size="36" class="flex-shrink-0">
                    <span class="text-white text-caption font-weight-bold">
                      {{ avatarLabel(msg) }}
                    </span>
                  </v-avatar>
  
                  <div class="flex-grow-1 min-w-0">
                    <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
                      <span class="font-weight-bold">{{ displaySender(msg) }}</span>
                      <v-chip
                        v-if="msg.type === 'bot'"
                        size="x-small"
                        color="warning"
                        variant="tonal"
                      >
                        AI
                      </v-chip>
                      <v-chip
                        v-if="msg.type === 'support'"
                        size="x-small"
                        color="primary"
                        variant="flat"
                      >
                        SUPPORT
                      </v-chip>
                      <v-chip v-if="msg.isChatLog" size="x-small" variant="outlined">
                        CHAT LOG
                      </v-chip>
                      <span class="text-caption text-medium-emphasis">• {{ msg.time }}</span>
                    </div>
  
                    <div class="message-bubble" :class="`bubble-${msg.type}`">
                      <div v-if="msg.text">{{ msg.text }}</div>
                      <div
                        v-if="messageAttachmentUrls(msg).length"
                        class="message-attachments"
                        :class="{ 'mt-2': msg.text }"
                      >
                        <template
                          v-for="(url, attachmentIndex) in messageAttachmentUrls(msg)"
                          :key="attachmentIndex"
                        >
                          <button
                            v-if="isImageAttachment(url)"
                            type="button"
                            class="attachment-link attachment-button"
                            @click="openImagePreview(url, msg.attachments)"
                          >
                            <img
                              :src="url"
                              :alt="`Attachment ${attachmentIndex + 1}`"
                              class="attachment-image"
                              loading="lazy"
                            />
                          </button>
                          <a
                            v-else
                            :href="url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="attachment-link attachment-file"
                          >
                            <v-chip
                              size="small"
                              variant="outlined"
                              prepend-icon="mdi-paperclip"
                            >
                              Attachment {{ attachmentIndex + 1 }}
                            </v-chip>
                          </a>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div ref="messagesEnd"></div>
          </div>
  
          <div v-else class="empty-correspondence">
            <v-icon size="40" color="grey-lighten-1">mdi-message-outline</v-icon>
            <div class="text-body-2 text-medium-emphasis mt-2">No correspondence yet.</div>
          </div>
        </div>
      </div>
  
      <!-- BOTTOM: Admin reply + resolved notice -->
      <div class="ticket-bottom">
        <div class="text-subtitle-1 font-weight-bold mb-3">Add correspondence</div>
        <div class="reply-box">
          <div class="reply-compose">
            <div v-if="replyImageAttachments.length" class="reply-compose-attachments">
              <div
                v-for="(item, index) in replyImageAttachments"
                :key="item.id"
                class="reply-attachment-item"
              >
                <button
                  type="button"
                  class="reply-attachment-btn"
                  @click="openImagePreview(item.previewUrl, [item.previewUrl])"
                >
                  <img :src="item.previewUrl" alt="Attachment preview" class="reply-attachment-image" />
                </button>
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  class="reply-attachment-remove"
                  :disabled="submitting || uploadingImages"
                  @click="removeReplyImage(index)"
                >
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <textarea
              v-model="replyText"
              ref="replyTextarea"
              class="reply-compose-textarea"
              placeholder="Type your response..."
              rows="3"
              :disabled="submitting || uploadingImages"
              @input="autoGrowReplyTextarea"
            ></textarea>
          </div>

          <div class="d-flex align-center justify-space-between flex-wrap mt-3" style="gap: 8px">
            <div>
              <input
                ref="replyImageInput"
                type="file"
                accept="image/*"
                multiple
                class="d-none"
                @change="onReplyImagesSelected"
              />
              <v-btn
                variant="outlined"
                prepend-icon="mdi-image-plus"
                :disabled="submitting || uploadingImages"
                @click="$refs.replyImageInput?.click()"
              >
                Upload image
              </v-btn>
            </div>
            <div class="d-flex" style="gap: 8px">
              <v-btn
                variant="outlined"
                :disabled="submitting || uploadingImages"
                @click="clearReplyForm"
              >
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                variant="flat"
                :disabled="(!replyText.trim() && !replyImageAttachments.length) || submitting || uploadingImages"
                :loading="submitting || uploadingImages"
                @click="submitReply"
              >
                Submit
              </v-btn>
            </div>
          </div>
        </div>
  
        <div
          v-if="isResolved || isAutoClosed"
          class="resolved-bar mt-4"
          :class="{ 'auto-closed-bar': isAutoClosed }"
        >
          <div class="text-body-2">
            <template v-if="isAutoClosed">
              <strong>Auto-closed</strong> — this case was closed due to inactivity. Only the
              reporter can reopen it from their side.
            </template>
            <template v-else>
              <strong>Marked Resolved</strong>
            </template>
          </div>
        </div>
      </div>
  
      <v-dialog v-model="imagePreview.show" max-width="960">
        <v-card class="image-preview-card">
          <v-card-title class="d-flex justify-space-between align-center pa-3">
            <span class="text-body-2 font-weight-medium">Attachment preview</span>
            <div class="d-flex align-center" style="gap: 4px">
              <template v-if="imagePreview.urls.length > 1">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  :disabled="imagePreview.index <= 0"
                  @click="showPrevImage"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <span class="text-caption text-medium-emphasis px-1">
                  {{ imagePreview.index + 1 }} / {{ imagePreview.urls.length }}
                </span>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  :disabled="imagePreview.index >= imagePreview.urls.length - 1"
                  @click="showNextImage"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </template>
              <v-btn icon variant="text" size="small" @click="closeImagePreview">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-card-title>
  
          <v-card-text class="image-preview-body pa-4">
            <img
              v-if="imagePreview.url"
              :src="imagePreview.url"
              alt="Attachment preview"
              class="preview-image"
            />
          </v-card-text>
  
          <!-- <v-card-actions class="justify-end pa-3 pt-0">
            <v-btn
              variant="text"
              color="primary"
              prepend-icon="mdi-open-in-new"
              :href="imagePreview.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open original
            </v-btn>
          </v-card-actions> -->
        </v-card>
      </v-dialog>
  
    </div>
  </template>
  
  <script>
  import { generateClient } from "aws-amplify/api";
  import { add_correspondence, get_upload_url } from "@/graphql/mutations";
  import ResolveTicket from "@/components/TicketManagement/ResolveTicket.vue";
  import GetTicket from "@/mixins/GetTicket";
  import { parseJson, mapChatbotLogs } from "@/mixins/ticketHelpers";

  function displayStatus(status) {
    if (!status) return "Open";
    const map = {
      OPEN: "Awaiting RCS Support",
      IN_PROGRESS: "In progress",
      PENDING_SUPPORT_ACTION: "Awaiting RCS Support",
      PENDING_CUSTOMER_ACTION: "Awaiting your reply",
      RESOLVED: "Resolved",
      AUTO_CLOSED: "Auto-closed",
    };
    return map[status] || status;
  }

  export default {
    name: "TicketReply",
    mixins: [GetTicket],
    components: {
      ResolveTicket,
    },
    props: {
      ticket: {
        type: Object,
        required: true,
      },
    },
    emits: ["back", "updated", "deleted"],
    data() {
      return {
        replyText: "",
        messages: [],
        submitting: false,
        resolveDialog: false,
        snackbar: {
          show: false,
          message: "",
          color: "success",
        },
        imagePreview: {
          show: false,
          url: "",
          urls: [],
          index: 0,
        },
        replyImageAttachments: [],
        uploadingImages: false,
        maxReplyImages: 5,
        showChatLogs: false,
        loadedTicketId: "",
      };
    },
    beforeUnmount() {
      this.clearReplyImages();
    },
    computed: {
      isResolved() {
        return this.statusLabel === "Resolved";
      },
      isAutoClosed() {
        return this.statusLabel === "Auto-closed";
      },
      statusLabel() {
        return displayStatus(this.ticket?.status || this.ticket?.statusRaw);
      },
      statusColor() {
        const map = {
          "Awaiting RCS Support": "info",
          "Awaiting your reply": "warning",
          "PENDING_CUSTOMER_ACTION": "warning",
          Resolved: "success",
          "Auto-closed": "grey",
          Open: "info",
          "In progress": "warning",
        };
        return map[this.statusLabel] || "primary";
      },
      statusIcon() {
        if (this.isResolved) return "mdi-check";
        if (this.isAutoClosed) return "mdi-timer-off-outline";
        if (
          this.statusLabel === "Awaiting your reply" ||
          this.statusLabel === "In progress"
        ) {
          return "mdi-progress-clock";
        }
        return "mdi-circle-outline";
      },
      severityChipColor() {
        const map = {
          Critical: "error",
          High: "warning",
          Normal: "success",
          Low: "grey",
        };
        return map[this.ticket?.severity] || "grey";
      },
      supportSender() {
        const user = this.$store?.state?.user || {};
        return (
          user.email ||
          user.user_email_id ||
          user.username ||
          user.name ||
          "Support"
        );
      },
      chatLogs() {
        const ticket = this.ticket || {};
        if (Array.isArray(ticket.chatbotLogs) && ticket.chatbotLogs.length) {
          return ticket.chatbotLogs;
        }
        return mapChatbotLogs(ticket.chatbot_logs || ticket.raw?.chatbot_logs || []);
      },
      chatLogMessages() {
        const reporter = this.ticket?.reporter || this.ticket?.reporterEmail || "User";
        const rows = [];
        this.chatLogs.forEach((log, index) => {
          if (log.query) {
            rows.push({
              id: `log-${log.id || index}-query`,
              isChatLog: true,
              type: "user",
              sender: reporter,
              time: log.time,
              text: log.query,
              attachments: [],
            });
          }
          if (log.response) {
            rows.push({
              id: `log-${log.id || index}-response`,
              isChatLog: true,
              type: "bot",
              sender: "RCS Support Bot",
              time: log.time,
              text: log.response,
              attachments: [],
            });
          }
        });
        return rows;
      },
      visibleMessages() {
        return this.showChatLogs ? [...this.chatLogMessages, ...this.messages] : this.messages;
      },
    },
    watch: {
      ticket: {
        immediate: true,
        handler(val) {
          const ticketId = val?.ticketId || val?.ticket_id || "";
          if (ticketId !== this.loadedTicketId) {
            this.loadedTicketId = ticketId;
            this.showChatLogs = false;
          }
          this.messages = [...(val?.messages || [])];
          this.replyText = "";
          this.scrollToLatest();
        },
      },
      messages() {
        this.scrollToLatest();
      },
    },
    methods: {
      toggleChatLogs() {
        this.showChatLogs = !this.showChatLogs;
        this.$nextTick(() => {
          if (!this.showChatLogs) {
            this.scrollToLatest();
            return;
          }
          const scroller = this.$refs.messagesScroll;
          if (scroller) scroller.scrollTop = 0;
        });
      },
      scrollToLatest() {
        this.$nextTick(() => {
          const end = this.$refs.messagesEnd;
          if (end?.scrollIntoView) {
            end.scrollIntoView({ block: "end", behavior: "smooth" });
            return;
          }
          const scroller = this.$refs.messagesScroll;
          if (scroller) {
            scroller.scrollTop = scroller.scrollHeight;
          }
        });
      },
      displaySender(msg) {
        const sender = String(msg?.author_name || msg?.sender || "").trim();
        if (sender && sender.toLowerCase() !== "unknown") return sender;
        if (msg?.type === "support") return this.supportSender;
        return sender || "Unknown";
      },
      avatarLabel(msg) {
        if (msg.type === "bot") return "AI";
        return (this.displaySender(msg) || "?").charAt(0).toUpperCase();
      },
      avatarColor(type) {
        if (type === "bot") return "warning";
        if (type === "support") return "primary";
        return "grey";
      },
      isImageAttachment(url) {
        if (!url) return false;
        const value = String(url);
        if (value.startsWith("blob:")) return true;
        const path = value.split("?")[0];
        return (
          /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(path) ||
          /ticket-attachments/i.test(value)
        );
      },
      messageAttachmentUrls(msg) {
        const list = Array.isArray(msg?.attachments) ? msg.attachments : [];
        return list.filter(Boolean);
      },
      autoGrowReplyTextarea(event) {
        const el = event?.target || this.$refs.replyTextarea;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
      },
      resetReplyTextareaHeight() {
        const el = this.$refs.replyTextarea;
        if (!el) return;
        el.style.height = "auto";
      },
      clearReplyImages() {
        this.replyImageAttachments.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        this.replyImageAttachments = [];
        if (this.$refs.replyImageInput) {
          this.$refs.replyImageInput.value = "";
        }
      },
      clearReplyForm() {
        this.replyText = "";
        this.clearReplyImages();
        this.$nextTick(() => this.resetReplyTextareaHeight());
      },
      onReplyImagesSelected(event) {
        const files = Array.from(event?.target?.files || []);
        if (!files.length) return;

        for (const file of files) {
          if (!String(file?.type || "").startsWith("image/")) {
            this.snackbar = {
              show: true,
              message: "Only image files can be attached.",
              color: "error",
            };
            continue;
          }
          if (this.replyImageAttachments.length >= this.maxReplyImages) {
            this.snackbar = {
              show: true,
              message: `You can attach up to ${this.maxReplyImages} images.`,
              color: "warning",
            };
            break;
          }
          this.replyImageAttachments.push({
            id: `${Date.now()}-${Math.random()}`,
            file,
            previewUrl: URL.createObjectURL(file),
          });
        }

        if (event?.target) {
          event.target.value = "";
        }
      },
      removeReplyImage(index) {
        const [removed] = this.replyImageAttachments.splice(index, 1);
        if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      },
      extractUploadConfig(responseData) {
        const data = responseData?.data || responseData;
        if (!data) return null;

        if (typeof data === "string") {
          return {
            uploadUrl: data.startsWith("http") ? data : "",
            path: data.startsWith("http") ? "" : data,
            contentType: "",
          };
        }

        const amplifyUpload = data?.amplify_upload || {};
        return {
          uploadUrl:
            data.upload_url ||
            data.uploadUrl ||
            data.presigned_url ||
            data.presignedUrl ||
            data.url ||
            "",
          path:
            amplifyUpload.path ||
            data.s3_key ||
            data.s3Key ||
            data.file_key ||
            data.fileKey ||
            data.path ||
            data.key ||
            "",
          contentType:
            amplifyUpload.contentType ||
            data.content_type ||
            data.contentType ||
            "",
        };
      },
      async getImageUploadConfig(file) {
        const client = generateClient();
        const response = await client.graphql({
          query: get_upload_url,
          variables: {
            input: {
              file_name: file.name,
            },
          },
        });

        const result = parseJson(response.data.get_upload_url) || {};
        if (result.status && result.status !== "SUCCESS") {
          throw new Error(result.status_message || "Failed to get upload URL");
        }

        const config = this.extractUploadConfig(result);
        if (!config?.uploadUrl && !config?.path) {
          throw new Error("Upload configuration was not returned");
        }
        return config;
      },
      async uploadImageFile(file) {
        const config = await this.getImageUploadConfig(file);
        if (!config.uploadUrl) {
          throw new Error(`No upload URL returned for ${file.name}`);
        }

        const response = await fetch(config.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type || config.contentType || "application/octet-stream",
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        return config.path || config.uploadUrl.split("?")[0];
      },
      async uploadReplyImages(attachments = []) {
        if (!attachments.length) return [];
        this.uploadingImages = true;
        try {
          const uploadedKeys = [];
          for (const attachment of attachments) {
            const uploadKey = await this.uploadImageFile(attachment.file);
            if (uploadKey) uploadedKeys.push(uploadKey);
          }
          return uploadedKeys;
        } finally {
          this.uploadingImages = false;
        }
      },
      openImagePreview(url, attachments = []) {
        const imageUrls = (attachments || []).filter((item) => this.isImageAttachment(item));
        const urls = imageUrls.length ? imageUrls : [url];
        const index = Math.max(0, urls.indexOf(url));
        this.imagePreview = {
          show: true,
          url: urls[index],
          urls,
          index,
        };
      },
      closeImagePreview() {
        this.imagePreview.show = false;
      },
      showPrevImage() {
        if (this.imagePreview.index <= 0) return;
        this.imagePreview.index -= 1;
        this.imagePreview.url = this.imagePreview.urls[this.imagePreview.index];
      },
      showNextImage() {
        if (this.imagePreview.index >= this.imagePreview.urls.length - 1) return;
        this.imagePreview.index += 1;
        this.imagePreview.url = this.imagePreview.urls[this.imagePreview.index];
      },
      async submitReply() {
        const text = this.replyText.trim();
        if ((!text && !this.replyImageAttachments.length) || this.submitting) {
          return;
        }

        this.submitting = true;
        try {
          const attachments = await this.uploadReplyImages(this.replyImageAttachments);
          const client = generateClient();
          const input = {
            ticket_id: this.ticket.ticketId || this.ticket.ticket_id,
            message: text || " ",
          };
          if (attachments.length) {
            input.attachments = attachments;
          }

          const response = await client.graphql({
            query: add_correspondence,
            variables: { input },
          });

          const responseData = parseJson(response.data.add_correspondence) || {};
          if (responseData.status != "SUCCESS") {
            throw new Error(responseData.status_message || "Failed to add correspondence");
          }

          const pendingImages = [...this.replyImageAttachments];
          this.replyText = "";
          this.replyImageAttachments = [];
          if (this.$refs.replyImageInput) {
            this.$refs.replyImageInput.value = "";
          }
          this.$nextTick(() => this.resetReplyTextareaHeight());

          const ticketId = this.ticket.ticketId || this.ticket.ticket_id;
          try {
            await this.fetchTicket(ticketId);
          } catch (error) {
            console.error("Failed to refresh ticket after reply:", error);
          }

          if (this.ticketDetail?.messages?.length) {
            pendingImages.forEach((item) => {
              if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            });
            this.messages = [...this.ticketDetail.messages];
          } else {
            this.messages.push({
              type: "support",
              sender: this.supportSender,
              author_name: this.supportSender,
              time: "Just now",
              text,
              attachments: pendingImages.map((item) => item.previewUrl),
            });
          }

          this.$emit("updated", {
            ...this.ticket,
            ...(this.ticketDetail || {}),
            messages: [...this.messages],
            updated: "Just now",
            status: "In progress",
            statusRaw: "PENDING_CUSTOMER_ACTION",
            assignedTo:
              this.ticket.assignedTo && this.ticket.assignedTo !== "-"
                ? this.ticket.assignedTo
                : this.supportSender,
          });
        } catch (error) {
          console.error("Failed to submit reply:", error);
          this.snackbar = {
            show: true,
            message: error.message || "Failed to add correspondence",
            color: "error",
          };
        } finally {
          this.submitting = false;
        }
      },
      handleResolved() {
        this.messages.push({
          type: "support",
          sender: this.supportSender,
          author_name: this.supportSender,
          time: "Just now",
          text: "Case marked as resolved.",
        });

        this.$emit("updated", {
          ...this.ticket,
          messages: [...this.messages],
          status: "Resolved",
          statusRaw: "RESOLVED",
          statusKey: "resolved",
          updated: "Just now",
        });
      },
    },
  };
  </script>
  
  <style scoped>
  .ticket-info-page {
    height: calc(100dvh - 64px);
    max-height: calc(100dvh - 64px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 8px 16px 12px;
    box-sizing: border-box;
    background: #fff;
  }
  
  .ticket-top {
    flex-shrink: 0;
  }
  
  .ticket-middle {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    overflow: hidden;
  }
  
  .correspondence-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .view-logs-btn {
    font-weight: 600 !important;
    letter-spacing: 0;
  }

  .logs-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    margin-bottom: 12px;
    border: 1px dashed #d7dde7;
    border-radius: 10px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
  }
  
  .messages-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
    padding-bottom: 12px;
  }
  
  .ticket-bottom {
    flex-shrink: 0;
    margin-top: auto;
    padding-top: 20px;
    padding-bottom: 8px;
    border-top: 1px solid #eceff3;
    background: #fff;
  }
  
  .meta-dot {
    color: rgba(0, 0, 0, 0.3);
  }
  
  .timeline {
    display: flex;
    flex-direction: column;
  }
  
  .timeline-item {
    display: flex;
    gap: 10px;
  }
  
  .timeline-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 14px;
    flex-shrink: 0;
    padding-top: 12px;
  }
  
  .timeline-node {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid #b0b8c4;
    background: #fff;
    z-index: 1;
  }
  
  .timeline-node.node-support {
    border-color: #1976d2;
    background: #1976d2;
  }
  
  .timeline-node.node-bot {
    border-color: #fb8c00;
    background: #fb8c00;
  }
  
  .timeline-line {
    width: 2px;
    flex: 1;
    min-height: 24px;
    background: #d7dde7;
    margin-top: 4px;
  }
  
  .timeline-content {
    flex: 1;
    padding-bottom: 16px;
    min-width: 0;
  }
  
  .timeline-item.is-last .timeline-content {
    padding-bottom: 8px;
  }
  
  .message-bubble {
    border: 1px solid #e6e8ee;
    border-radius: 12px;
    padding: 12px 14px;
    font-size: 14px;
    line-height: 1.55;
    color: rgba(0, 0, 0, 0.78);
    background: #fff;
    white-space: pre-wrap;
  }
  
  .bubble-support {
    background: #f5f9ff;
    border-color: #cfe0f5;
  }
  
  .bubble-bot {
    background: #fff8f0;
    border-color: #ffe0b2;
  }
  
  .message-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .attachment-link {
    display: block;
    line-height: 0;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e0e0e0;
    transition: box-shadow 0.15s ease;
  }
  
  .attachment-link:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
  
  .attachment-button {
    padding: 0;
    background: none;
    cursor: pointer;
  }
  
  .attachment-file {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    text-decoration: none;
  }
  
  .attachment-image {
    display: block;
    max-width: 220px;
    max-height: 160px;
    width: auto;
    height: auto;
    object-fit: cover;
  }
  
  .image-preview-card {
    overflow: hidden;
  }
  
  .image-preview-body {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    max-height: 75vh;
    overflow: auto;
    background: #f5f5f5;
  }
  
  .preview-image {
    display: block;
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
    object-fit: contain;
  }
  
  .empty-correspondence {
    text-align: center;
    padding: 40px 16px;
  }
  
  .reply-box {
    border: 1px solid #e6e8ee;
    border-radius: 12px;
    padding: 14px;
    background: #fff;
  }

  .reply-compose {
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 12px;
    background: #fff;
    min-height: 96px;
  }

  .reply-compose-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
  }

  .reply-attachment-item {
    position: relative;
  }

  .reply-attachment-btn {
    border: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
    display: block;
  }

  .reply-attachment-image {
    width: 88px;
    height: 88px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    display: block;
    background: #fafafa;
  }

  .reply-attachment-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    background: rgba(255, 255, 255, 0.9) !important;
  }

  .reply-compose-textarea {
    width: 100%;
    display: block;
    border: 0;
    outline: none;
    resize: none;
    min-height: 72px;
    padding: 0;
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: rgba(0, 0, 0, 0.87);
    background: transparent;
    font-family: inherit;
  }

  .reply-compose-textarea::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }

  .reply-compose-textarea:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .resolved-bar {
    border: 1px solid #c8e6c9;
    background: #f1f8f2;
    border-radius: 14px;
    padding: 18px 20px;
  }
  
  .auto-closed-bar {
    border-color: #cfd8dc;
    background: #f5f7f8;
  }
  
  .min-w-0 {
    min-width: 0;
  }
  
  @media (max-width: 600px) {
    .header-meta .meta-dot {
      display: none;
    }
  
    .ticket-info-page {
      height: calc(100dvh - 64px);
      max-height: calc(100dvh - 64px);
    }
  }
  </style>
  